import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { userV2API } from '../services/api';
import theme, { tokens } from '../theme';
import { ONBOARDING_DESTINATIONS, resolveOnboardingDestination } from '../utils/onboardingFlow';

const steps = ['Goals', 'Safety'];

const goalOptions = [
  { value: 'lose_weight', label: 'Lose weight' },
  { value: 'maintain_weight', label: 'Maintain weight' },
  { value: 'gain_weight', label: 'Gain weight' },
  { value: 'metabolic_health', label: 'Improve metabolic health' },
  { value: 'general_wellness', label: 'General wellness' },
];

const experienceOptions = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const dietStyleOptions = [
  { value: 'balanced', label: 'Balanced' },
  { value: 'high_protein', label: 'High protein' },
  { value: 'mediterranean', label: 'Mediterranean' },
  { value: 'plant_forward', label: 'Plant-forward' },
];

const mealTypeOptions = [
  { value: 'protein_focused', label: 'Protein-focused meal' },
  { value: 'light_snack', label: 'Light snack' },
  { value: 'breakfast', label: 'Breakfast foods' },
  { value: 'lunch', label: 'Lunch-style meal' },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, previewMode } = useAuth();
  const [bootstrapping, setBootstrapping] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [goals, setGoals] = useState({
    primaryGoal: '',
    targetWeight: '',
    targetTimeline: '',
    weeklyPace: 'moderate',
    fastingExperience: 'beginner',
    motivation: '',
  });
  const [nutrition, setNutrition] = useState({
    dietStyle: 'balanced',
    preferredFastBreakingMealType: 'protein_focused',
  });
  const [safety, setSafety] = useState({
    pregnancyOrBreastfeeding: false,
    diabetes: false,
    bloodPressureIssues: false,
    eatingDisorderRisk: false,
    medications: '',
  });

  const progress = ((activeStep + 1) / steps.length) * 100;

  useEffect(() => {
    let cancelled = false;

    const loadOnboarding = async () => {
      // /onboarding is wrapped in PrivateRoute only, not OnboardingGate, so a
      // preview visitor (truthy user, no accessToken/refreshToken) reaches
      // this effect directly. previewMode comes from AuthContext (captured
      // once at session establishment); without this check `!user?.id` never
      // catches the preview user and userV2API.getOnboarding 401s, which the
      // axios interceptor treats as a real session expiring. Preview mode has
      // no real onboarding state, so send it straight to the dashboard like
      // OnboardingGate does.
      if (previewMode) {
        navigate(
          { pathname: user?.isAdmin ? '/admin/overview' : '/dashboard/daily-state', search: location.search },
          { replace: true }
        );
        return;
      }

      if (!user?.id) {
        setBootstrapping(false);
        return;
      }

      try {
        const res = await userV2API.getOnboarding(user.id);
        const onboarding = res.data?.data;
        const completedSteps = onboarding?.completedSteps || [];
        const destination = resolveOnboardingDestination(completedSteps);

        if (destination === ONBOARDING_DESTINATIONS.HOME) {
          navigate(user?.isAdmin ? '/admin/overview' : '/dashboard/daily-state', { replace: true });
          return;
        }

        if (!cancelled) {
          setActiveStep(destination === ONBOARDING_DESTINATIONS.SAFETY ? 1 : 0);
        }

        const [goalsRes, nutritionRes, safetyRes] = await Promise.allSettled([
          userV2API.getGoals(user.id),
          userV2API.getNutritionPreferences(user.id),
          userV2API.getHealthContext(user.id),
        ]);

        if (cancelled) return;

        if (goalsRes.status === 'fulfilled') {
          const data = goalsRes.value.data?.data || {};
          setGoals((current) => ({
            ...current,
            primaryGoal: data.primaryGoal || current.primaryGoal,
            targetWeight: data.targetWeight ?? '',
            targetTimeline: data.targetTimeline || '',
            weeklyPace: data.weeklyPace || current.weeklyPace,
            fastingExperience: data.fastingExperience || current.fastingExperience,
            motivation: data.motivation || '',
          }));
        }

        if (nutritionRes.status === 'fulfilled') {
          const data = nutritionRes.value.data?.data || {};
          setNutrition((current) => ({
            ...current,
            dietStyle: data.dietStyle || current.dietStyle,
            preferredFastBreakingMealType:
              data.preferredFastBreakingMealType || current.preferredFastBreakingMealType,
          }));
        }

        if (safetyRes.status === 'fulfilled') {
          const data = safetyRes.value.data?.data || {};
          setSafety((current) => ({
            ...current,
            pregnancyOrBreastfeeding: Boolean(data.pregnancyOrBreastfeeding),
            diabetes: Boolean(data.diabetes),
            bloodPressureIssues: Boolean(data.bloodPressureIssues),
            eatingDisorderRisk: Boolean(data.eatingDisorderRisk),
            medications: data.medications || '',
          }));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Failed to load onboarding');
        }
      } finally {
        if (!cancelled) {
          setBootstrapping(false);
        }
      }
    };

    loadOnboarding();

    return () => {
      cancelled = true;
    };
  }, [location.search, navigate, previewMode, user?.id, user?.isAdmin]);

  const finishOnboarding = async (completedSteps) => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      await userV2API.patchOnboarding(user.id, { completedSteps, version: 1 });
      navigate(user?.isAdmin ? '/admin/overview' : '/dashboard/daily-state', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save onboarding');
    } finally {
      setLoading(false);
    }
  };

  const handleGoalsNext = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      await userV2API.patchGoals(user.id, {
        primaryGoal: goals.primaryGoal,
        targetWeight: goals.targetWeight ? parseFloat(goals.targetWeight) : null,
        targetTimeline: goals.targetTimeline || null,
        weeklyPace: goals.weeklyPace,
        fastingExperience: goals.fastingExperience,
        motivation: goals.motivation,
      });
      await userV2API.patchNutritionPreferences(user.id, {
        dietStyle: nutrition.dietStyle,
        preferredFastBreakingMealType: nutrition.preferredFastBreakingMealType,
      });
      await userV2API.patchOnboarding(user.id, {
        completedSteps: ['profile', 'goals', 'nutrition'],
        version: 1,
      });
      setActiveStep(1);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save goals');
    } finally {
      setLoading(false);
    }
  };

  const handleSafetySubmit = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      await userV2API.patchHealthContext(user.id, {
        pregnancyOrBreastfeeding: safety.pregnancyOrBreastfeeding,
        diabetes: safety.diabetes,
        bloodPressureIssues: safety.bloodPressureIssues,
        eatingDisorderRisk: safety.eatingDisorderRisk,
        medications: safety.medications,
      });
      await finishOnboarding(['profile', 'goals', 'nutrition', 'safety']);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save safety context');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    const completedSteps = activeStep === 0
      ? ['profile', 'goals', 'nutrition']
      : ['profile', 'goals', 'nutrition', 'safety'];
    await finishOnboarding(completedSteps);
  };

  if (bootstrapping) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: tokens.bg,
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: 4,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 560,
          bgcolor: tokens.surface,
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          boxShadow: theme.shadows[4],
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Personalize your plan
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
        </Typography>
        <LinearProgress variant="determinate" value={progress} sx={{ mb: 3, borderRadius: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {activeStep === 0 && (
          <Stack spacing={2}>
            <TextField
              select
              label="Primary goal"
              value={goals.primaryGoal}
              onChange={(e) => setGoals({ ...goals, primaryGoal: e.target.value })}
              fullWidth
            >
              {goalOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Target weight (kg, optional)"
              type="number"
              value={goals.targetWeight}
              onChange={(e) => setGoals({ ...goals, targetWeight: e.target.value })}
              fullWidth
            />
            <TextField
              label="Target timeline (optional)"
              placeholder="e.g. 3 months"
              value={goals.targetTimeline}
              onChange={(e) => setGoals({ ...goals, targetTimeline: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Fasting experience"
              value={goals.fastingExperience}
              onChange={(e) => setGoals({ ...goals, fastingExperience: e.target.value })}
              fullWidth
            >
              {experienceOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="What motivated you to join? (optional)"
              multiline
              minRows={2}
              value={goals.motivation}
              onChange={(e) => setGoals({ ...goals, motivation: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Eating style"
              value={nutrition.dietStyle}
              onChange={(e) => setNutrition({ ...nutrition, dietStyle: e.target.value })}
              fullWidth
            >
              {dietStyleOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Preferred fast-breaking meal"
              value={nutrition.preferredFastBreakingMealType}
              onChange={(e) => setNutrition({ ...nutrition, preferredFastBreakingMealType: e.target.value })}
              fullWidth
            >
              {mealTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Optional safety context helps FastTime avoid unsuitable recommendations.
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={safety.pregnancyOrBreastfeeding}
                  onChange={(e) => setSafety({ ...safety, pregnancyOrBreastfeeding: e.target.checked })}
                />
              )}
              label="Pregnant or breastfeeding"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={safety.diabetes}
                  onChange={(e) => setSafety({ ...safety, diabetes: e.target.checked })}
                />
              )}
              label="Diabetes or prediabetes"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={safety.bloodPressureIssues}
                  onChange={(e) => setSafety({ ...safety, bloodPressureIssues: e.target.checked })}
                />
              )}
              label="Blood pressure concerns"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={safety.eatingDisorderRisk}
                  onChange={(e) => setSafety({ ...safety, eatingDisorderRisk: e.target.checked })}
                />
              )}
              label="History of disordered eating"
            />
            <TextField
              label="Relevant medications (optional)"
              multiline
              minRows={2}
              value={safety.medications}
              onChange={(e) => setSafety({ ...safety, medications: e.target.value })}
              fullWidth
            />
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
          <Button variant="text" onClick={handleSkip} disabled={loading}>
            Skip for now
          </Button>
          {activeStep === 0 ? (
            <Button variant="contained" onClick={handleGoalsNext} disabled={loading || !goals.primaryGoal}>
              Continue
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSafetySubmit} disabled={loading}>
              Finish
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
